import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Spacing4, Spacing6 } from 'autogen/design-tokens/tokens';
import type { ProductListVariantsModel, VariantDisplayType } from 'autogen/swagger/all';
import { Typography } from 'components/generic/typography';
import SwatchRenderer from 'components/product/Variants/Swatches/SwatchRenderer';
import type { SwatchSize } from 'components/product/Variants/Swatches/shared/types';
import useHiddenItemsObserver from 'hooks/globals/useHiddenItemsObserver';
import { removeListStyling } from 'modules/helpers/mixins';

// #region Styles
const Nav = styled.nav`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${Spacing6};
    overflow: hidden;
    container-type: inline-size;
`;

const remainingAmountWidth = 32;
const colorSquareSize = 24;
const List = styled.ul<{ $displayType: VariantDisplayType; $size: SwatchSize }>`
    ${removeListStyling}
    display: flex;
    flex-wrap: wrap;
    overflow: hidden;
    gap: ${Spacing4};
    padding-right: 1px; // hidden item fix
    max-height: 24px;

    // Fix number floating right on mobile
    ${({ $displayType, theme }) => {
        const gap = Number.parseInt(theme?.Spacing4 ?? '4');
        const totalWidth = (count: number) =>
            colorSquareSize * count +
            gap * (count - 1) +
            remainingAmountWidth +
            Number.parseInt(theme?.Spacing6 ?? '6');
        const sizeOf3ColorSquares = totalWidth(3);
        const sizeOf4ColorSquares = totalWidth(4);
        const sizeOf5ColorSquares = totalWidth(5);

        return (
            $displayType === 'Color' &&
            css`               
                @container (width <= ${sizeOf3ColorSquares}px) {
                    // room for 2 color squares
                    li:nth-child(n+3) {
                        display: none;
                    }
                }

                @container (${sizeOf3ColorSquares}px <= width <= ${sizeOf4ColorSquares}px) {
                    // room for 3 color squares
                    li:nth-child(n+4) {
                        display: none;
                    }
                }

                @container (${sizeOf4ColorSquares}px <= width <= ${sizeOf5ColorSquares}px) {
                    // room for 4 color squares
                    li:nth-child(n+5) {
                        display: none;
                    }
                }
            `
        );
    }}
`;

const ListItem = styled.li<{ $displayType: VariantDisplayType; $lastVisibleItem: number }>`
    max-width: 100%;
    overflow: hidden;

    ${({ $displayType }) =>
        $displayType === 'Text' &&
        css`
            flex-grow: 1;
        `}
`;

const StyledLabel = styled(Typography.Label)`
    flex-shrink: 0;
    min-width: ${remainingAmountWidth}px; // To stop re-rendering loop
    line-height: 0; // to truly align center
    overflow: visible;
    padding: ${({ theme }) => theme.Spacing4};
`;
// #endregion Styles

export default function MoreVariants({
    variants,
    size = 'sm',
    ...restProps
}: {
    variants: ProductListVariantsModel;
    size?: SwatchSize;
}) {
    const ref = useRef<HTMLUListElement>(null);
    const hiddenItems = useHiddenItemsObserver({ ref, observeChildren: true });
    const { totalAmount, variantList } = variants;
    const remainingAmount = totalAmount - (variantList.length - hiddenItems);
    const lastVisibleItem = variantList.length - hiddenItems;
    const { displayType } = variantList[0];

    return (
        <Nav {...restProps}>
            <List ref={ref} $displayType={displayType} $size={size}>
                {variantList.map((variant) => (
                    <ListItem key={variant.productId} $displayType={displayType} $lastVisibleItem={lastVisibleItem}>
                        <SwatchRenderer {...variant} size={size} />
                    </ListItem>
                ))}
            </List>
            {remainingAmount > 0 && <StyledLabel size="S">+{remainingAmount}</StyledLabel>}
        </Nav>
    );
}
